/**
 * This file defines the fireErrorAnalytics() service.
 */
import type { ErrorInfo } from 'react';

import type { GasPurePayload } from '@atlaskit/analytics-gas-types';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';

import type {
	AbstractServiceMarker,
	AnyServiceWithDeps,
	DependenciesContext,
} from '../../core/types';

import { createAnalyticsEvent, type EnablementTouchpoint } from './common';

export type AnalyticsMeta = {
	id: string;
	packageName?: string;
	teamName?: string;
};

export type AnalyticsAttributes = {
	[key: string]:
		| boolean
		| string
		| number
		| AnalyticsAttributes
		| AnalyticsAttributes[]
		| undefined
		| null;
};

export type AnalyticsPayload = {
	attributes?: AnalyticsAttributes;
	error?: Error;
	errorInfo?: ErrorInfo;
	event?: UIAnalyticsEvent;
	meta: AnalyticsMeta;
	sendToPrivacyUnsafeSplunk?: boolean;
	skipSentry?: boolean;
};

type FireErrorAnalyticsDeps = {
	createAnalyticsEvent: CreateUIAnalyticsEvent & AbstractServiceMarker;
};

const fireErrorAnalyticsDependencies: FireErrorAnalyticsDeps = {
	createAnalyticsEvent,
};

export const fireErrorAnalytics: typeof fireErrorAnalyticsImpl & {
	dependencies: FireErrorAnalyticsDeps;
} = Object.assign(fireErrorAnalyticsImpl, {
	dependencies: fireErrorAnalyticsDependencies,
}) satisfies AnyServiceWithDeps<FireErrorAnalyticsDeps>;

// Still allows an async override of fireErrorAnalytics, but the default implementation is synchronous.
function fireErrorAnalyticsImpl(
	input: AnalyticsPayload,
	context: DependenciesContext<FireErrorAnalyticsDeps>,
): void {
	const touchpoint = input.attributes?.touchpoint as EnablementTouchpoint | undefined;
	context
		.dependencies()
		.createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				actionSubject: input.meta.id,
				action: 'failed',
				...(touchpoint ? { source: touchpoint } : {}),
				attributes: {
					...input.attributes,
					...(input.error && { message: input.error.message }),
				},
				tags: ['growth'],
			} satisfies GasPurePayload,
		})
		.fire();
}
